import {
  deriveSessionStatus,
  trainingFailure,
  validLocalDate,
} from '../domain/training.ts';

import type {
  SessionStatus,
  TrainingResult,
} from '../domain/training.ts';

import type {
  TrainingRepository,
} from '../ports/training-repository.ts';

import type {
  HomeTrainingAction,
  HomeTrainingSummary,
  TrainingHomeSource,
} from '../ports/home-sources.ts';

function sameContext(
  a: {
    datasetId: string;
    controlEpoch: number;
  },
  b: {
    datasetId: string;
    controlEpoch: number;
  },
) {
  return (
    a.datasetId === b.datasetId &&
    a.controlEpoch === b.controlEpoch
  );
}

function actionFor(
  localDate: string,
  status?: SessionStatus,
  executionId?: string,
): HomeTrainingAction {
  if (status === undefined) {
    return {
      label: 'Planificar sesión',
      href: `#Training/Planificar/${localDate}`,
    };
  }

  if (status === 'EN_CURSO' && executionId) {
    return {
      label: 'Continuar sesión',
      href: `#Training/Sesion/${executionId}`,
    };
  }

  if (
    (status === 'COMPLETADA' || status === 'INCOMPLETA') &&
    executionId
  ) {
    return {
      label: 'Consultar ejecución',
      href: `#Training/Ejecucion/${executionId}`,
    };
  }

  if (status === 'PLANIFICADA') {
    return {
      label: 'Abrir sesión planificada',
      href: `#Training/Hoy/${localDate}`,
    };
  }

  return {
    label: 'Consultar Training',
    href: `#Training/Hoy/${localDate}`,
  };
}

export class PublicTrainingHome
implements TrainingHomeSource {
  constructor(
    private readonly planning: Pick<
      TrainingRepository,
      'readPlannedSessionByDate'
    >,

    private readonly catalog: Pick<
      TrainingRepository,
      'readContext' |
      'readRoutineRevision'
    >,

    private readonly sessions: Pick<
      TrainingRepository,
      'readExecutionForPlannedSession'
    >,
  ) {}

  async readTrainingHome(
    localDate: string,
  ): Promise<
    TrainingResult<HomeTrainingSummary>
  > {
    if (!validLocalDate(localDate)) {
      return trainingFailure(
        'VALIDATION_ERROR',
      );
    }

    const before =
      await this.catalog.readContext();

    if (!before.ok) {
      return before;
    }

    const planned =
      await this.planning
        .readPlannedSessionByDate(
          localDate,
        );

    if (!planned.ok) {
      return planned;
    }

    if (
      planned.value &&
      !sameContext(
        planned.value.context,
        before.value,
      )
    ) {
      return trainingFailure(
        'DATASET_STALE',
      );
    }

    if (!planned.value) {
      const after =
        await this.catalog.readContext();

      if (
        !after.ok ||
        !sameContext(
          before.value,
          after.value,
        )
      ) {
        return after.ok
          ? trainingFailure(
              'DATASET_STALE',
            )
          : after;
      }

      return {
        ok: true,
        value: {
          kind: 'NO_PLAN',
          localDate,
          action: actionFor(localDate),
          context: { ...before.value },
        },
      };
    }

    const execution =
      await this.sessions
        .readExecutionForPlannedSession(
          planned.value.session.sessionId,
        );

    if (!execution.ok) {
      return execution;
    }

    if (
      execution.value &&
      !sameContext(
        execution.value.context,
        before.value,
      )
    ) {
      return trainingFailure(
        'DATASET_STALE',
      );
    }

    const status =
      deriveSessionStatus(
        planned.value.session,
        execution.value?.execution,
      );

    if (!status.ok) {
      return status;
    }

    const routine =
      await this.catalog
        .readRoutineRevision(
          planned.value.session
            .routineRevisionRef,
        );

    if (!routine.ok) {
      return routine;
    }

    const after =
      await this.catalog.readContext();

    if (
      !after.ok ||
      !sameContext(
        before.value,
        after.value,
      )
    ) {
      return after.ok
        ? trainingFailure(
            'DATASET_STALE',
          )
        : after;
    }

    const executionId =
      execution.value?.execution.executionId;

    return {
      ok: true,
      value: {
        kind: 'SESSION',

        sessionId:
          planned.value.session.sessionId,

        plannedLocalDate:
          planned.value.session
            .plannedLocalDate,

        routineName:
          routine.value.name,

        status:
          status.value,

        ...(executionId
          ? { executionId }
          : {}),

        action: actionFor(
          planned.value.session.plannedLocalDate,
          status.value,
          executionId,
        ),

        context: { ...before.value },
      },
    };
  }
}
